import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Calendar, ArrowRight, Tag } from 'lucide-react';

const Blog = () => {
  const { themeColors } = useTheme();
  const [email, setEmail] = useState('');

  const containerStyle = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px'
  };

  const contentStyle = {
    maxWidth: '1200px',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const titleStyle = {
    color: themeColors.text,
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subtitleStyle = {
    color: themeColors.textSecondary,
    fontSize: '20px',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto'
  };

  const sectionStyle = {
    marginBottom: '60px'
  };

  const sectionTitleStyle = {
    color: themeColors.text,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '24px'
  };

  const blogGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: '32px',
    marginBottom: '24px'
  };

  const blogCardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s'
  };

  const accentBarStyle = {
    height: '6px',
    backgroundColor: themeColors.primary
  };

  const blogContentStyle = {
    padding: '24px'
  };

  const categoryTagStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    backgroundColor: themeColors.primary + '15',
    color: themeColors.primary,
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    marginBottom: '12px'
  };

  const blogTitleStyle = {
    color: themeColors.text,
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '8px',
    lineHeight: '1.4'
  };

  const dateStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: themeColors.textSecondary,
    fontSize: '14px',
    marginBottom: '12px'
  };

  const excerptStyle = {
    color: themeColors.textSecondary,
    fontSize: '15px',
    lineHeight: '1.6',
    marginBottom: '16px'
  };

  const readMoreStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    color: themeColors.primary,
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'none'
  };

  const newsletterCardStyle = {
    background: `linear-gradient(135deg, ${themeColors.primary} 0%, ${themeColors.accent} 100%)`,
    borderRadius: '16px',
    padding: '48px 32px',
    textAlign: 'center',
    color: '#ffffff'
  };

  const inputStyle = {
    width: '100%',
    maxWidth: '400px',
    padding: '14px 20px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '16px',
    marginBottom: '16px'
  };

  const buttonStyle = {
    backgroundColor: '#ffffff',
    color: themeColors.primary,
    padding: '14px 32px',
    borderRadius: '8px',
    border: 'none',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.2s'
  };

  const posts = [
    {
      category: 'Tips & Tricks',
      title: '10 Things to Check Before Buying a Used Car Seat',
      date: 'March 15, 2026',
      excerpt: 'Safety comes first when it comes to your little one. Here\'s our comprehensive checklist for evaluating pre-owned car seats before you buy.'
    },
    {
      category: 'Sustainability',
      title: 'How Buying Pre-Loved Baby Items Helps the Planet',
      date: 'March 8, 2026',
      excerpt: 'The average family spends over $12,000 on baby items in the first year alone. Learn how choosing second-hand makes a difference for your wallet and the environment.'
    },
    {
      category: 'Seller Tips',
      title: 'How to Take Photos That Sell: A Seller\'s Guide',
      date: 'February 28, 2026',
      excerpt: 'Great photos are the #1 factor in getting your items sold quickly. We break down lighting, angles, and staging tips that work.'
    },
    {
      category: 'Community',
      title: 'Meet Our Top Sellers of 2026',
      date: 'February 20, 2026',
      excerpt: 'These parents have built thriving shops on BabyResell. Learn their secrets to success and how they turned decluttering into extra income.'
    },
    {
      category: 'Product Updates',
      title: 'New Features: Seasonal Themes & Improved Search',
      date: 'February 10, 2026',
      excerpt: 'We\'ve been busy! Check out the latest updates to BabyResell including our beautiful seasonal themes and more powerful search filters.'
    },
    {
      category: 'Safety',
      title: 'CPSC Recalls You Need to Know About in 2026',
      date: 'January 30, 2026',
      excerpt: 'Staying informed about product recalls is essential for every parent. Here\'s a roundup of the most important baby product recalls this year.'
    }
  ];

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>BabyResell Blog</h1>
          <p style={subtitleStyle}>
            Tips, stories, and updates from the BabyResell community. Learn how to buy smart, sell better, and make the most of your parenting journey.
          </p>
        </div>

        {/* Featured Posts */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Featured Posts</h2>
          <div style={blogGridStyle}>
            {posts.map((post, index) => (
              <div
                key={index}
                style={blogCardStyle}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.15)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                }}
              >
                <div style={accentBarStyle} />
                <div style={blogContentStyle}>
                  <div style={categoryTagStyle}>
                    <Tag size={12} />
                    {post.category}
                  </div>
                  <h3 style={blogTitleStyle}>{post.title}</h3>
                  <div style={dateStyle}>
                    <Calendar size={14} />
                    {post.date}
                  </div>
                  <p style={excerptStyle}>{post.excerpt}</p>
                  <div style={readMoreStyle}>
                    Read More <ArrowRight size={14} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Subscribe to Newsletter */}
        <div style={sectionStyle}>
          <div style={newsletterCardStyle}>
            <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginBottom: '16px' }}>
              Subscribe to Our Newsletter
            </h2>
            <p style={{ fontSize: '16px', marginBottom: '32px', opacity: 0.95 }}>
              Stay updated with the latest tips, community stories, and BabyResell news.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <input
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={inputStyle}
              />
              <button
                style={buttonStyle}
                onMouseOver={(e) => e.target.style.opacity = '0.9'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
              >
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
